import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Car, Plus, Pencil, Trash2, Loader2, Zap, Unlink } from 'lucide-react';
import { BottomSheet } from '@evi/ui';
import { MenuIcon } from '@/components/primitives';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toast';
import {
  fetchMyVehicles, createMyVehicle, updateMyVehicle, deleteMyVehicle,
  fetchAutochargeCredentials, revokeAutocharge,
  type Vehicle, type VehicleConnector, type CreateVehicleBody, type UpdateVehicleBody,
  type AutochargeCredential,
} from '@/lib/api';
import formStyles from '@/components/Form.module.css';
import styles from './MyEvPage.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

interface Props { onNavigate: (path: string) => void }

const CONNECTORS: VehicleConnector[] = ['Type2', 'CCS', 'CHAdeMO'];
const VEHICLES_KEY = ['my-vehicles'] as const;

/**
 * My EV — manage the driver's personal vehicles. A clean CRUD list: add, view,
 * edit, and remove a car the driver owns outside any fleet, against the
 * /me/vehicles route. Empty / loading / error states are all first-class. No
 * telematics or charging stats here; this page only manages the vehicle record.
 */
export function MyEvPage({ onNavigate }: Props) {
  const { t } = useTranslation('vehicle');
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: VEHICLES_KEY,
    queryFn: fetchMyVehicles,
    staleTime: 60_000,
  });
  const vehicles = data ?? [];

  // The add/edit sheet target: null = closed, 'new' = add, a Vehicle = edit it.
  const [formFor, setFormFor] = useState<Vehicle | 'new' | null>(null);
  // The vehicle pending a delete confirm, or null.
  const [deleteFor, setDeleteFor] = useState<Vehicle | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: VEHICLES_KEY });

  const removeVehicle = async (v: Vehicle): Promise<void> => {
    await deleteMyVehicle(v._id);
    await refresh();
    toast(t('toast.removed'), { tone: 'accent' });
    setDeleteFor(null);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('loading')
              : t('vehicleCount', { count: vehicles.length })}
          </p>
        </div>
      </header>

      {isLoading && <VehiclesLoading />}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('loadError')}
        </div>
      )}

      {!isLoading && !error && vehicles.length === 0 && (
        <VehiclesEmpty onAdd={() => setFormFor('new')} />
      )}

      {!isLoading && !error && vehicles.length > 0 && (
        <>
          <div className={styles.list}>
            {vehicles.map((v) => (
              <VehicleCard
                key={v._id}
                vehicle={v}
                onEdit={() => setFormFor(v)}
                onRemove={() => setDeleteFor(v)}
              />
            ))}
          </div>
          <button type="button" className={styles.addBtn} onClick={() => setFormFor('new')}>
            <Plus size={16} /> {t('addAnother')}
          </button>
        </>
      )}

      <AutochargeSection />

      <VehicleFormSheet
        target={formFor}
        onClose={() => setFormFor(null)}
        onSaved={async () => { await refresh(); setFormFor(null); }}
      />

      {deleteFor && (
        <ConfirmDialog
          title={t('remove.title')}
          body={t('remove.body', { name: `${deleteFor.make} ${deleteFor.model}` })}
          confirmLabel={t('remove.confirm')}
          confirmTone="danger"
          onConfirm={() => removeVehicle(deleteFor)}
          onCancel={() => setDeleteFor(null)}
        />
      )}
    </div>
  );
}

function VehicleCard({
  vehicle, onEdit, onRemove,
}: { vehicle: Vehicle; onEdit: () => void; onRemove: () => void }) {
  const { t } = useTranslation('vehicle');
  const meta = `${vehicle.year} · ${vehicle.connector_standard}${vehicle.label ? ` · ${vehicle.label}` : ''}`;
  return (
    <div className={styles.card}>
      <div className={styles.cardIcon} aria-hidden><Car size={22} /></div>
      <div className={styles.cardBody}>
        <p className={styles.cardTitle}>{vehicle.make} {vehicle.model}</p>
        <p className={styles.cardMeta}>{meta}</p>
      </div>
      <div className={styles.cardActions}>
        <button type="button" className={styles.iconBtn} aria-label={t('card.edit')} onClick={onEdit}>
          <Pencil size={15} />
        </button>
        <button
          type="button"
          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
          aria-label={t('card.remove')}
          onClick={onRemove}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

const AUTOCHARGE_KEY = ['autocharge', 'credentials'] as const;

/**
 * Plug & charge — the cars linked to this account by vehicle id
 * (docs/tickets/ocpp-autocharge-vid.md). Linking happens on the charging
 * screen after a plug-in; this section lists and unlinks. The raw id is
 * never shown: a credential is its label plus the saved vehicle it is
 * attached to, if any. A row paused by a model-level rule says so rather
 * than looking like something the driver did.
 */
function AutochargeSection() {
  const { t } = useTranslation('vehicle');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: AUTOCHARGE_KEY,
    queryFn: fetchAutochargeCredentials,
    staleTime: 60_000,
  });
  const [revokeFor, setRevokeFor] = useState<AutochargeCredential | null>(null);
  // A car EVI support switched off stays listed (and labelled): it cannot be
  // linked back from a session, so hiding it would make it vanish for good.
  const creds = (data ?? []).filter((c) => c.is_active || c.suspended || c.pending || c.disabled_by_admin);

  const revoke = async (c: AutochargeCredential): Promise<void> => {
    try {
      await revokeAutocharge(c._id);
      await qc.invalidateQueries({ queryKey: AUTOCHARGE_KEY });
      toast(t('autocharge.revokedToast'), { tone: 'accent' });
    } catch (err) {
      toast(err instanceof Error ? err.message : t('loadError'), { tone: 'error' });
    } finally {
      setRevokeFor(null);
    }
  };

  if (isLoading) return null;
  return (
    <section className={styles.autocharge} aria-labelledby="autocharge-title">
      <h2 id="autocharge-title" className={styles.sectionTitle}>
        <Zap size={15} aria-hidden /> {t('autocharge.title')}
      </h2>
      <p className={styles.sectionDesc}>{t('autocharge.desc')}</p>
      {creds.length === 0 && <p className={styles.sectionEmpty}>{t('autocharge.empty')}</p>}
      {creds.length > 0 && (
        <div className={styles.list}>
          {creds.map((c) => (
            <div key={c._id} className={styles.card}>
              <div className={styles.cardIcon} aria-hidden><Zap size={20} /></div>
              <div className={styles.cardBody}>
                <p className={styles.cardTitle}>{c.label ?? t('autocharge.unnamed')}</p>
                <p className={styles.cardMeta}>
                  {c.vehicle ? `${c.vehicle.year} ${c.vehicle.make} ${c.vehicle.model}` : t('autocharge.noVehicle')}
                  {c.suspended ? ` · ${t('autocharge.suspended')}` : c.pending ? ` · ${t('autocharge.pending')}` : c.disabled_by_admin ? ` · ${t('autocharge.disabledByAdmin')}` : ''}
                </p>
              </div>
              {/* A car EVI support turned off is support's to manage: no
                  Unlink (its "link it again" promise would be false). */}
              {!c.disabled_by_admin && (
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                    aria-label={t('autocharge.revoke')}
                    onClick={() => setRevokeFor(c)}
                  >
                    <Unlink size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {revokeFor && (
        <ConfirmDialog
          title={t('autocharge.revokeTitle')}
          body={t('autocharge.revokeBody')}
          confirmLabel={t('autocharge.revokeConfirm')}
          confirmTone="danger"
          onConfirm={() => revoke(revokeFor)}
          onCancel={() => setRevokeFor(null)}
        />
      )}
    </section>
  );
}

function VehiclesEmpty({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation('vehicle');
  return (
    <div className={styles.empty}>
      {/* My EV icon slot — the my-ev animated WebP (the submenu's My EV row
          moved to Account Settings, which has no icon slot, so the icon lives
          on the vehicle page itself; poster under reduced motion). */}
      <div className={styles.emptyIcon} aria-hidden><MenuIcon name="my-ev" fill alt="" /></div>
      <h2 className={styles.emptyTitle}>{t('empty.title')}</h2>
      <p className={styles.emptyDesc}>
        {t('empty.desc')}
      </p>
      <button type="button" className={styles.emptyCta} onClick={onAdd}>
        <Plus size={16} /> {t('empty.cta')}
      </button>
    </div>
  );
}

function VehiclesLoading() {
  return (
    <div className={styles.list} aria-busy aria-live="polite">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className={styles.cardSkeleton} />
      ))}
    </div>
  );
}

function VehicleFormSheet({
  target, onClose, onSaved,
}: {
  target: Vehicle | 'new' | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation('vehicle');
  const isOpen = target !== null;
  const editing = target && target !== 'new' ? target : null;
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={editing ? t('form.editTitle') : t('form.addTitle')} glass>
      {/* Mounts fresh on each open (and re-keys per target) so the form starts
          from the right initial values. */}
      {isOpen && (
        <VehicleForm
          key={editing?._id ?? 'new'}
          editing={editing}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </BottomSheet>
  );
}

function VehicleForm({
  editing, onClose, onSaved,
}: {
  editing: Vehicle | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation('vehicle');
  const [make, setMake] = useState(editing?.make ?? '');
  const [model, setModel] = useState(editing?.model ?? '');
  const [year, setYear] = useState(editing ? String(editing.year) : '');
  const [connector, setConnector] = useState<VehicleConnector>(editing?.connector_standard ?? 'Type2');
  const [label, setLabel] = useState(editing?.label ?? '');
  const [vin, setVin] = useState(editing?.vin ?? '');
  const [battery, setBattery] = useState(
    editing?.battery_capacity_kwh != null ? String(editing.battery_capacity_kwh) : '',
  );
  const [power, setPower] = useState(
    editing?.max_charge_power_kw != null ? String(editing.max_charge_power_kw) : '',
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useFormError();

  const yearNum = Number(year);
  const canSubmit =
    make.trim() !== '' && model.trim() !== ''
    && Number.isInteger(yearNum) && yearNum >= 1900 && yearNum <= 2100
    && !submitting;

  // Optional fields: a filled value, or null to clear on edit, or omitted on create.
  const optionalStr = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  const optionalNum = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      if (editing) {
        const body: UpdateVehicleBody = {
          make: make.trim(),
          model: model.trim(),
          year: yearNum,
          connector_standard: connector,
          label: optionalStr(label),
          vin: optionalStr(vin),
          battery_capacity_kwh: optionalNum(battery),
          max_charge_power_kw: optionalNum(power),
        };
        await updateMyVehicle(editing._id, body);
        toast(t('toast.updated'), { tone: 'accent' });
      } else {
        const body: CreateVehicleBody = {
          make: make.trim(),
          model: model.trim(),
          year: yearNum,
          connector_standard: connector,
          ...(label.trim() ? { label: label.trim() } : {}),
          ...(vin.trim() ? { vin: vin.trim() } : {}),
          ...(battery.trim() ? { battery_capacity_kwh: Number(battery) } : {}),
          ...(power.trim() ? { max_charge_power_kw: Number(power) } : {}),
        };
        await createMyVehicle(body);
        toast(t('toast.added'), { tone: 'accent' });
      }
      await onSaved();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('form.saveError'));
      setSubmitting(false);
    }
  };

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} noValidate>
      {errorMsg && <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>}

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="ev-make">{t('form.make')}</label>
        <input
          id="ev-make" className={formStyles.input} value={make}
          onChange={(e) => setMake(e.target.value)}
          placeholder={t('form.makePlaceholder')} maxLength={100} disabled={submitting} required
        />
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="ev-model">{t('form.model')}</label>
        <input
          id="ev-model" className={formStyles.input} value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder={t('form.modelPlaceholder')} maxLength={100} disabled={submitting} required
        />
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="ev-year">{t('form.year')}</label>
        <input
          id="ev-year" className={formStyles.input} type="number" inputMode="numeric"
          value={year} onChange={(e) => setYear(e.target.value)}
          placeholder={t('form.yearPlaceholder')} min={1900} max={2100} disabled={submitting} required
        />
      </div>

      <div className={formStyles.field}>
        <span className={formStyles.label}>{t('form.connector')}</span>
        <div className={styles.chipGroup} role="radiogroup" aria-label={t('form.connectorAria')}>
          {CONNECTORS.map((opt) => (
            <button
              key={opt} type="button" role="radio" aria-checked={connector === opt}
              className={connector === opt ? styles.chipActive : styles.chip}
              onClick={() => setConnector(opt)} disabled={submitting}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button" className={styles.advancedToggle}
        onClick={() => setShowAdvanced((s) => !s)} aria-expanded={showAdvanced}
      >
        {showAdvanced ? t('form.hideOptional') : t('form.addOptional')}
      </button>

      {showAdvanced && (
        <div className={styles.advanced}>
          <div className={formStyles.field}>
            <label className={formStyles.label} htmlFor="ev-label">{t('form.nickname')}</label>
            <input
              id="ev-label" className={formStyles.input} value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('form.nicknamePlaceholder')} maxLength={100} disabled={submitting}
            />
          </div>
          <div className={formStyles.field}>
            <label className={formStyles.label} htmlFor="ev-vin">{t('form.vin')}</label>
            <input
              id="ev-vin" className={formStyles.input} value={vin}
              onChange={(e) => setVin(e.target.value)}
              placeholder={t('form.optionalPlaceholder')} maxLength={32} disabled={submitting}
            />
          </div>
          <div className={formStyles.field}>
            <label className={formStyles.label} htmlFor="ev-battery">{t('form.battery')}</label>
            <input
              id="ev-battery" className={formStyles.input} type="number" inputMode="decimal"
              value={battery} onChange={(e) => setBattery(e.target.value)}
              placeholder={t('form.batteryPlaceholder')} min={0} disabled={submitting}
            />
          </div>
          <div className={formStyles.field}>
            <label className={formStyles.label} htmlFor="ev-power">{t('form.power')}</label>
            <input
              id="ev-power" className={formStyles.input} type="number" inputMode="decimal"
              value={power} onChange={(e) => setPower(e.target.value)}
              placeholder={t('form.powerPlaceholder')} min={0} disabled={submitting}
            />
          </div>
        </div>
      )}

      <div className={styles.formActions}>
        <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={submitting}>
          {t('form.cancel')}
        </button>
        <button type="submit" className={formStyles.btnPrimary} disabled={!canSubmit}>
          {submitting
            ? <><Loader2 size={14} className={styles.spin} /> {t('form.saving')}</>
            : (editing ? t('form.save') : t('form.add'))}
        </button>
      </div>
    </form>
  );
}
